import { useEffect } from "react";
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { useNavigate, Navigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { api } from "../../../../convex/_generated/api";
import { useAppAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowRight } from "lucide-react";

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const { hasOrganization, hasPersonalWorkspace, isLoading: authLoading } = useAppAuth();
  const createPersonalWorkspace = useMutation(api.personalWorkspaces.create);

  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    contactPhone: "",
    industry: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to dashboard if user already has a workspace or org
  useEffect(() => {
    if (!authLoading && (hasOrganization || hasPersonalWorkspace)) {
      navigate("/dashboard");
    }
  }, [authLoading, hasOrganization, hasPersonalWorkspace, navigate]);

  if (!authLoading && (hasOrganization || hasPersonalWorkspace)) {
    return null;
  }

  const formatPhoneNumber = (value: string) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await createPersonalWorkspace({
        companyName: formData.companyName,
        contactName: formData.contactName || undefined,
        contactEmail: clerkUser?.primaryEmailAddress?.emailAddress || undefined,
        contactPhone: formData.contactPhone || undefined,
        industry: formData.industry || undefined,
      });
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create workspace");
      setIsLoading(false);
    }
  };

  // Show loading while Clerk initializes or AuthContext loads
  if (!clerkLoaded || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect if not authenticated via Clerk
  useEffect(() => {
    if (clerkLoaded && !clerkUser) {
      navigate("/sign-in");
    }
  }, [clerkLoaded, clerkUser, navigate]);

  if (clerkLoaded && !clerkUser) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-12">
      <Card className="w-full max-w-lg border-0 shadow-xl">
        <CardHeader className="pb-2 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-primary/5">
            <img src="/mc-icon.svg" alt="ManageClean Logo" className="h-8 w-8 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold">Welcome to ManageClean</CardTitle>
          <CardDescription className="text-base">
            Let's set up your workspace. You can add team members later by upgrading to a team plan.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-sm font-medium">
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="companyName"
                value={formData.companyName}
                onChange={(e) => setFormData((prev) => ({ ...prev, companyName: e.target.value }))}
                placeholder="Your Company Name"
                className="h-11"
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactName" className="text-sm font-medium">
                Your Name
              </Label>
              <Input
                id="contactName"
                value={formData.contactName}
                onChange={(e) => setFormData((prev) => ({ ...prev, contactName: e.target.value }))}
                placeholder="John Smith"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactPhone" className="text-sm font-medium">
                Phone
              </Label>
              <Input
                id="contactPhone"
                value={formData.contactPhone}
                onChange={(e) => setFormData((prev) => ({ ...prev, contactPhone: formatPhoneNumber(e.target.value) }))}
                placeholder="(555) 123-4567"
                maxLength={14}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry" className="text-sm font-medium">
                Industry
              </Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) => setFormData((prev) => ({ ...prev, industry: e.target.value }))}
                placeholder="Commercial Cleaning"
                className="h-11"
              />
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="h-12 w-full text-base font-medium"
              disabled={isLoading || !formData.companyName}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
